import clsx from "clsx";
import useCustomLoginErrorHandler from "hooks/useCustomLoginErrorHandler";
import Cookies from "universal-cookie";
import config from "config";
import addRefererToBodyRequest from "helpers/addRefererToBodyRequest";
import ToastHtmlComponent from "components/Reusable/ToastHtmlComponent/ToastHtmlComponent";
import GoogleLoginButton from "components/Structure/SocialMediaAuthentication/components/GoogleLoginButton";
import MicrosoftLoginButton from "components/Structure/SocialMediaAuthentication/components/MicrosoftLoginButton";
import FormValidationMessage from "components/Reusable/Form/FormValidationMessage";

import { memo, useState } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { useToasts } from "react-toast-notifications";
import { useModal } from "contexts/modalContext/modalContext";
import { useUser } from "contexts/userContext/userContext";
import type { AuthenticationOptionsT } from "models/models";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { CustomDomainPagesE } from "models/enums";
import { APIExternalLoginApiRequest } from "api/UsersController";
import { MainDomainPagesE } from "models/enums";

import styles from "./thirdPartyAuth.module.scss";

interface ThirdPartyAuthI extends React.HTMLAttributes<HTMLDivElement> {
  onSubmitSuccess?: (...args: unknown[]) => void;
  onSubUserSubmitSuccess?: (email: string) => void;
  orgAuthOptions?: AuthenticationOptionsT;
  inviteDomainName?: string;
  showOrDivider?: boolean;
  setEmailNotBelongToOrg?: (value: string) => void;
}

const cookies = new Cookies();

const isInviteByLinkPage = window.location.pathname.includes(
  CustomDomainPagesE.INVITE
);
const isInviteByEmailPage = window.location.pathname.includes(
  CustomDomainPagesE.SUB_USER_COMPLETE
);

const ThirdPartyAuth = memo((props: ThirdPartyAuthI) => {
  const {
    onSubmitSuccess,
    onSubUserSubmitSuccess,
    orgAuthOptions,
    inviteDomainName,
    className,
    showOrDivider = true,
    setEmailNotBelongToOrg,
    ...restProps
  } = props;

  const {
    getBasicSettingsData,
    getTextByKey,
    getPath,
    getCurrentPage,
    getLanguage,
  } = useContent();
  const { addToast } = useToasts();
  const { closeModal } = useModal();
  const { setUser, logout } = useUser();

  const [errorResponse, setErrorResponse] = useState(null);

  const handleCustomError = useCustomLoginErrorHandler();

  const handleApiAuth = async (body) => {
    addRefererToBodyRequest(body);

    const payload = {
      ...body,
      isInvitationPage: isInviteByLinkPage,
      inviteByEmailToken: window.location.pathname.includes(
        CustomDomainPagesE.SUB_USER_COMPLETE
      )
        ? window.location.pathname.split("/")[3]
        : null,
    };

    const response = await APIExternalLoginApiRequest(payload);

    if (response.status === "success") {
      const userName = response.data.UserData.UserName;
      const hasSameOrg =
        response.data.UserData.UserName.includes(inviteDomainName);
      const isEmailVerified = response.data.UserData.IsConfirm;
      const isSubscribed = response.data.UserData.IsSubscribed;
      const isSubUser = response.data.UserData.IsSubUser;

      // Set auth token to cookies
      document.cookie =
        config.api.jumbomailApiAuthCookie +
        "=" +
        response.data?.BearerToken +
        ";path=/";

      // Check for custom domain redirection
      const userBucketName = response.data.UserData.BucketName;

      if (userBucketName && window.location.origin !== userBucketName) {
        // If current page is page from MainDomainPagesE
        if (
          Object.values(MainDomainPagesE).some((page) =>
            window.location.pathname.includes(page)
          )
        ) {
          const currLang = window.location.pathname.split("/")[1];
          logout();
          window.location.href = `${userBucketName}/${currLang}/home`;
          return {
            status: "success",
          };
        }

        logout();
        window.location.href = `${userBucketName}${window.location.pathname}`;
        return {
          status: "success",
        };
      }

      // Implementation of the logic according to
      // https://docs.google.com/spreadsheets/d/1d1_xT5uO2RsRdr3PLHgakaCSG8ageNN6UHqaBL_jPGM/edit#gid=0
      //
      // for Sign In with the invitation link.

      if (isInviteByLinkPage) {
        // Cases 1, 2 and 3.
        // If domain coincides with the org domain
        // and user isn't subscriber yet and email is verified.
        // In other words - FULL SUCCESS
        if (hasSameOrg && !isSubscribed && isEmailVerified) {
          await onSubUserSubmitSuccess?.(userName);
        }

        // Cases 4 and 5.
        // The same as above but email isn't verified.
        // SUCCESS, but sub user status will be PENDING.
        else if (hasSameOrg && !isSubscribed && !isEmailVerified) {
          await onSubUserSubmitSuccess?.(userName);
          window.location.href =
            config.baseurl + `/${getLanguage()}/registration-complete`;
        }

        // Cases 6, 7 and 8.
        // ACCESS DENIED because of not correct domain.

        // Handles in the InvitationLinkCard.js
        // else if (!hasSameOrg) {}

        // Cases 9 and 10.
        // If already is subscribed

        // Handles in the InvitationLinkCard.js
        // else if (hasSameOrg && isEmailVerified && isSubscribed) {}

        // Case 11.
        // If already is sub user and email is verified.
        else if (isSubUser && isEmailVerified) {
          window.location.href = config.baseurl + `/${getLanguage()}/inbox`;
        }

        // Case 12.
        // If already is sub user but email isn't verified.
        else if (isSubUser && !isEmailVerified) {
          window.location.href =
            config.baseurl + `/${getLanguage()}/registration-complete`;
        }
      }

      cookies.set("loginDate", response.data.nowDate, {
        path: "/",
        domain: ".jumbomail.me",
      });

      if (onSubmitSuccess) {
        localStorage.setItem("proccessSignUpEmbedPage", true.toString());
      }

      setUser(response.data);

      if (onSubmitSuccess) {
        if (response.data) {
          const payload = {
            UserData: response.data.UserData,
            IsRegistered: true,
          };

          onSubmitSuccess(payload);
        }
      } else if (response.data.IsRegistered) {
        window.location.href = config.baseurl + getPath() + "home";
      }
      addToast(getTextByKey("sign-in-success"), { appearance: "success" });

      if (getCurrentPage() === "registration" && !response.data.IsRegistered) {
        window.location.href = config.baseurl + getPath() + "home";
      }
      if (getCurrentPage() === "login") {
        window.location.href = config.baseurl + getPath() + "home";
      }
      closeModal();

      if (isInviteByEmailPage) {
        // update sub user data in the top user sub user list: make user active and verified
        onSubUserSubmitSuccess?.(userName);
      }

      return {
        callBack: closeModal,
        status: "success",
      };
    } else {
      if (response.data.includes("not_under_domain")) {
        // get user email by not_under_domain-userEmail
        const userEmail = response.data?.replace("not_under_domain-", "");

        setEmailNotBelongToOrg(`${userEmail}`);
      } else if (
        response?.data?.includes("microsoft") ||
        response?.data?.includes("google") ||
        response?.data?.includes("sub-user-must-join-organization")
      ) {
        handleCustomError(response.data);
      } else {
        // let errText = getTextByKey("api-error");
        // errText = errText.replace("#ERRORNUM#", 1);
        // errText = errText.replace("#SYSMAIL#", "contact-us");
        // errText = errText.replace("#SYSMAIL#", config.supportmail);
        // errText = errText.replace("#SYSMAIL#", "contact-us");
        // addToast(<ToastHtmlComponent htmlStr={errText} />, {
        //   appearance: "error",
        // });
        setErrorResponse([
          {
            message: response.data,
            status: response.status,
          },
        ]);
      }

      return {
        message: response.message,
        status: response.status,
      };
    }
  };

  return getBasicSettingsData().googleClientId &&
    getBasicSettingsData().microsoftClientId ? (
    <GoogleOAuthProvider clientId={getBasicSettingsData().googleClientId}>
      <>
        {showOrDivider &&
        (orgAuthOptions
          ? orgAuthOptions.GoogleSSO || orgAuthOptions.MicrosoftSSO
          : false) ? (
          <div className={styles.orDivider}>{getTextByKey("or")}</div>
        ) : null}
        <div className={clsx(styles.wrapper, className)} {...restProps}>
          {(orgAuthOptions ? orgAuthOptions?.GoogleSSO : true) && (
            <GoogleLoginButton handleApiAuth={handleApiAuth} />
          )}
          {(orgAuthOptions ? orgAuthOptions?.MicrosoftSSO : true) && (
            <MicrosoftLoginButton handleApiAuth={handleApiAuth} />
          )}
        </div>
        {errorResponse && (
          <div className={styles.externalLoginErrorContainer}>
            <FormValidationMessage errorMessageObj={errorResponse} />
          </div>
        )}
      </>
    </GoogleOAuthProvider>
  ) : null;
});

export default ThirdPartyAuth;
